import { useQuery } from '@apollo/client';
import { Search } from '@mui/icons-material';
import { Button, FormControl, FormLabel, Grid2, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ShibUser } from '@thro-types/users';

import { Field, Formik, useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import USERS_QUERY from 'gql/queries/getUsers.gql';

import MultistepForm from 'app/components/MultistepForm';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';

import { Modal } from '../Modal';
import { userInitialValues, userValidationSchema } from './validation';

interface ModalValues {
  email: string;
  firstName: string;
  lastName: string;
  studentID: string;
  uid: string;
  user?: ShibUser;
}

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleSubmit: (user: ShibUser) => void;
  additionalFilter?: { [key: string]: unknown };
  title?: string;
  hideMtrNumberField?: boolean;
  hideUidField?: boolean;
}

export const UserSelectionModal = (props: Props) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [hasEnteredSearchTerm, setHasEnteredSearchTerm] = useState(false);

  const [usersResult, setUsersResult] = useState<ShibUser[]>([]);

  const { refetch, loading, previousData } = useQuery(USERS_QUERY, {
    fetchPolicy: 'no-cache',
    context: { clientName: 'federation' },
    notifyOnNetworkStatusChange: true,
    skip: true
  });

  const handleSubmit = async (values: ModalValues) => {
    if (submitting || !values.user) {
      return;
    }
    setSubmitting(true);
    props.handleSubmit(values.user);
    setSubmitting(false);
  };

  const steps = ['Users'];

  const Content = () => {
    const { values, touched, errors, handleChange, setFieldValue } =
      useFormikContext<ModalValues>();

    const handleRefetch = async () => {
      setHasEnteredSearchTerm(true);
      const result = await refetch({
        filter:
          values.email || values.studentID || values.uid
            ? JSON.stringify({
                email: { term: values.email },
                studentID: { term: values.studentID },
                uid: { term: values.uid }
              })
            : undefined,
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        type: UserType.SHIBBOLETH,
        offset: 0,
        limit: 50,
        ...props.additionalFilter
      });
      setUsersResult(result.data.users);
    };

    return (
      <Grid2 container gap={3}>
        <Grid2 container size={3.5} gap={1}>
          <Grid2 size={12}>
            <FormControl fullWidth>
              <FormLabel>{t(translations.common.firstName())}</FormLabel>
              <Field
                value={values.firstName}
                as={TextField}
                fullWidth
                id="firstName"
                name="firstName"
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                onChange={handleChange}
                size="small"
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            <FormControl fullWidth>
              <FormLabel>{t(translations.common.lastName())}</FormLabel>
              <Field
                value={values.lastName}
                as={TextField}
                fullWidth
                id="lastName"
                name="lastName"
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                onChange={handleChange}
                size="small"
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            <FormControl fullWidth>
              <FormLabel>{t(translations.common.email())}</FormLabel>
              <Field
                value={values.email}
                as={TextField}
                fullWidth
                id="email"
                name="email"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                onChange={handleChange}
                size="small"
              />
            </FormControl>
          </Grid2>
          {!props.hideMtrNumberField && (
            <Grid2 size={12}>
              <FormControl fullWidth>
                <FormLabel>{t(translations.common.mtrNumber())}</FormLabel>
                <Field
                  value={values.studentID}
                  as={TextField}
                  fullWidth
                  id="studentID"
                  name="studentID"
                  error={touched.studentID && Boolean(errors.studentID)}
                  helperText={touched.studentID && errors.studentID}
                  onChange={handleChange}
                  size="small"
                />
              </FormControl>
            </Grid2>
          )}
          <Grid2 container justifyContent={'flex-end'} size={12} alignItems={'center'}>
            <Button
              variant={'contained'}
              startIcon={<Search />}
              onClick={handleRefetch}
              type="submit"
            >
              {t(translations.common.search())}
            </Button>
          </Grid2>
        </Grid2>
        {!hasEnteredSearchTerm ? (
          <Grid2 container size={8} justifyContent={'center'} alignItems={'center'}>
            {t(translations.common.enterSearchTerm())}
          </Grid2>
        ) : (
          <DataGrid
            sx={{ maxHeight: '30em' }}
            rows={usersResult}
            columns={[
              {
                field: 'firstName',
                headerName: t(translations.common.firstName()),
                flex: 1,
                sortable: false
              },
              {
                field: 'lastName',
                headerName: t(translations.common.lastName()),
                flex: 1,
                sortable: false
              },
              {
                field: 'email',
                headerName: t(translations.common.email()),
                flex: 1,
                sortable: false
              }
            ]}
            hideFooter
            disableColumnMenu
            onRowSelectionModelChange={ids => {
              const selectedIDs = new Set(ids);
              const selectedRowData = usersResult.filter(row => selectedIDs.has(row.id));

              if (selectedRowData.length > 0) {
                setFieldValue('user', selectedRowData[0]);
              }
            }}
            loading={loading && !!previousData}
          />
        )}
      </Grid2>
    );
  };

  const handleClose = () => {
    setHasEnteredSearchTerm(false);
    props.setOpen(false);
  };

  return (
    <Modal
      open={props.open}
      headline={props.title ? props.title : t(translations.common.addUser())}
      onClose={(_event, reason) => {
        if (reason && reason === 'backdropClick') return;
        props.setOpen(false);
      }}
      onCancel={handleClose}
    >
      <Grid2 px={3}>
        <Formik
          validationSchema={yup.object().shape(userValidationSchema(t))}
          onSubmit={values => {
            handleSubmit(values);
          }}
          initialValues={userInitialValues()}
          validateOnMount
        >
          <MultistepForm
            submitting={submitting}
            steps={steps}
            hideStepper={true}
            onCancel={handleClose}
            customInvalidFormTooltip={t(translations.pages.modules.alerts.addStudentInvalidForm())}
          >
            {() => <Content />}
          </MultistepForm>
        </Formik>
      </Grid2>
    </Modal>
  );
};
